import type { Query, QueryClient, QueryKey } from "@tanstack/react-query";

/** Keeps each tab's `me` true to the session cookie. On every first page load (router.tsx), so
 *  React Query types only; the sign-out code loads when a session has to end. */

/** How long `me` counts as current, and how often a signed-in tab that is in view asks again. */
export const ME_REFRESH_MS = 60_000;
const RECHECK_GAP_MS = 5_000;
const CHANNEL = "alm.session";

type SessionEvent = { type: "signed-out" } | { type: "signed-in" };

interface Revalidates {
  invalidate: () => Promise<unknown>;
}

let client: QueryClient | null = null;
let router: Revalidates | null = null;
let seen: { userId: string; print: string } | null = null;
let lastRecheck = 0;
let leaving = false;
let channel: BroadcastChannel | null | undefined;

export function isMeQuery(queryKey: QueryKey): boolean {
  const [path] = queryKey;
  return Array.isArray(path) && path.length === 2 && path[0] === "auth" && path[1] === "me";
}

function statusOf(error: unknown): number | null {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : null;
}

function inBrowser(): boolean {
  return typeof window !== "undefined";
}

/** One channel per tab: a channel never hears its own posts, so a tab never follows itself. */
function sessionChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  try {
    channel = new BroadcastChannel(CHANNEL);
  } catch {
    channel = null;
  }
  return channel;
}

/** Tells this browser's other tabs that the person in them changed. */
export function announceSession(event: SessionEvent): void {
  try {
    sessionChannel()?.postMessage(event);
  } catch {
    // Best effort: the idle guard's shared stamp still carries a sign-out to the other tabs.
  }
}

/** A sign-in just succeeded: nothing cached for whoever was here before may survive it. */
export function beginSession(queryClient: QueryClient): void {
  seen = null;
  queryClient.clear();
  announceSession({ type: "signed-in" });
}

/** Who this browser acts as changed (signing in as somebody, entering an organization): every
 *  tab starts again, this one at `to`. */
export function switchSession(queryClient: QueryClient, to: string): void {
  leaving = true;
  queryClient.clear();
  announceSession({ type: "signed-in" });
  window.location.assign(to);
}

/** This tab is on its way out; nothing here should start a second exit. */
export function markLeaving(): void {
  leaving = true;
}

function signedInHere(): boolean {
  if (!client) return false;
  return client
    .getQueriesData({ predicate: (query) => isMeQuery(query.queryKey) })
    .some(([, data]) => data !== undefined);
}

function hereNow(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/** The session is gone on the server: the cookie goes too, and the next page starts from
 * nothing. */
function sessionEnded(): void {
  if (leaving) return;
  leaving = true;
  const target = `/login?reason=ended&next=${encodeURIComponent(hereNow())}`;
  void import("@/lib/session/sign-out")
    .then(({ endSession }) => endSession(client, target))
    .catch(() => window.location.assign(target));
}

/** A refusal may mean the role changed or the session ended: ask `me`, at most every few
 * seconds. */
function recheck(): void {
  if (!client || leaving || !signedInHere()) return;
  const now = Date.now();
  if (now - lastRecheck < RECHECK_GAP_MS) return;
  lastRecheck = now;
  void client.refetchQueries({
    predicate: (query) => isMeQuery(query.queryKey) && query.state.data !== undefined,
  });
}

function followOtherTab(event: MessageEvent<SessionEvent>): void {
  if (leaving) return;
  if (event.data?.type === "signed-out") {
    const wasSignedIn = signedInHere();
    client?.clear();
    if (!wasSignedIn) return;
    leaving = true;
    window.location.assign(`/login?next=${encodeURIComponent(hereNow())}`);
    return;
  }
  if (
    event.data?.type === "signed-in" &&
    (signedInHere() || window.location.pathname === "/login")
  ) {
    leaving = true;
    window.location.reload();
  }
}

/** Refusals that can mean the person's access changed: out of scope answers as not found. */
const ACCESS_ANSWERS = new Set([401, 403, 404]);

export function onQueryError(error: unknown, query: Query<unknown, unknown, unknown, QueryKey>) {
  if (!inBrowser()) return;
  const status = statusOf(error);
  if (isMeQuery(query.queryKey)) {
    if (status === 401 && query.state.data !== undefined) sessionEnded();
    return;
  }
  if (status === null || !ACCESS_ANSWERS.has(status)) return;
  recheck();
  // Refused on a refetch: stop showing the record, which they may not open any more.
  if (status !== 401 && query.state.data !== undefined) {
    const queryKey = query.queryKey;
    setTimeout(() => client?.removeQueries({ queryKey, exact: true }), 0);
  }
}

export function onMutationError(error: unknown): void {
  if (!inBrowser()) return;
  const status = statusOf(error);
  if (status !== null && ACCESS_ANSWERS.has(status)) recheck();
}

/** What decides what they may see: a new answer with the same of this changes nothing. */
function accessPrint(data: unknown): string {
  const me = data as {
    subject?: unknown;
    activeOrganization?: { id?: unknown } | null;
    user?: { mustResetPassword?: unknown; role?: unknown };
  };
  return JSON.stringify([
    me.subject,
    me.activeOrganization?.id ?? null,
    me.user?.mustResetPassword ?? null,
    me.user?.role ?? null,
  ]);
}

export function onQuerySuccess(data: unknown, query: Query<unknown, unknown, unknown, QueryKey>) {
  if (!inBrowser() || !isMeQuery(query.queryKey) || !data) return;
  const userId = (data as { user?: { id?: unknown } }).user?.id;
  const now = { userId: typeof userId === "string" ? userId : "", print: accessPrint(data) };
  const before = seen;
  seen = now;
  if (!before || before.print === now.print || leaving) return;
  if (before.userId !== now.userId) {
    // Everything this tab holds belongs to somebody else.
    leaving = true;
    window.location.reload();
    return;
  }
  // What they may see changed: nothing fetched under the old answer is shown again, and every
  // route guard runs against the new one.
  client?.removeQueries({ predicate: (cached) => !isMeQuery(cached.queryKey) });
  void router?.invalidate();
}

/** Wires the watch to this tab's cache and router; a no-op on the server. */
export function watchSession(queryClient: QueryClient, appRouter: Revalidates): void {
  if (!inBrowser()) return;
  client = queryClient;
  router = appRouter;
  sessionChannel()?.addEventListener("message", followOtherTab);
}
